import { definePreset } from '@primeuix/themes';
import Nora from '@primeuix/themes/nora';

const VidaPreset = definePreset(Nora, {
  // ─── Primitive Tokens ─── Custom VIDA red palette
  primitive: {
    vidaRed: {
      50: '#fff5f5',
      100: '#ffe0e0',
      200: '#ffb3b3',
      300: '#ff8080',
      400: '#e60000',
      500: '#cc0000',
      600: '#b30000',
      700: '#990000',
      800: '#800000',
      900: '#660000',
      950: '#4d0000'
    },
    borderRadius: {
      none: '0',
      xs: '0',
      sm: '0',
      md: '0',
      lg: '0',
      xl: '0'
    }
  },

  // ─── Semantic Tokens ───
  semantic: {
    primary: {
      50: '{vidaRed.50}',
      100: '{vidaRed.100}',
      200: '{vidaRed.200}',
      300: '{vidaRed.300}',
      400: '{vidaRed.400}',
      500: '{vidaRed.500}',
      600: '{vidaRed.600}',
      700: '{vidaRed.700}',
      800: '{vidaRed.800}',
      900: '{vidaRed.900}',
      950: '{vidaRed.950}'
    },
    transitionDuration: '0s',
    formField: {
      borderRadius: '0',
      shadow: '0 3px 6px rgba(168, 168, 168, 0.2)'
    },
    focusRing: {
      width: '0',
      style: 'none',
      color: 'transparent',
      offset: '0',
      shadow: 'none'
    },
    content: {
      borderRadius: '0'
    },
    overlay: {
      select: {
        borderRadius: '0'
      },
      popover: {
        borderRadius: '0'
      },
      modal: {
        borderRadius: '0'
      }
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '#fafafa',
          100: '#eceff0',
          200: '#d4dadd',
          300: '#cccccc',
          400: '#7f878d',
          500: '#6d757a',
          600: '#43494e',
          700: '#363636',
          800: '#292829',
          900: '#212529',
          950: '#000000'
        },
        primary: {
          color: '#cc0000',
          contrastColor: '#ffffff',
          hoverColor: '#b30000',
          activeColor: '#990000'
        },
        highlight: {
          background: '#cc0000',
          focusBackground: '#b30000',
          color: '#ffffff',
          focusColor: '#ffffff'
        },
        formField: {
          background: '#ffffff',
          disabledBackground: '{surface.200}',
          filledBackground: '{surface.100}',
          filledHoverBackground: '{surface.100}',
          filledFocusBackground: '{surface.100}',
          borderColor: '#d6d6d6',
          hoverBorderColor: '#d6d6d6',
          focusBorderColor: '#d6d6d6',
          invalidBorderColor: '#f1322e',
          color: '{surface.900}',
          disabledColor: '{surface.500}',
          placeholderColor: '{surface.500}',
          floatLabelColor: '{surface.500}',
          floatLabelFocusColor: '{primary.color}',
          floatLabelActiveColor: '{surface.500}',
          iconColor: '{surface.900}',
          shadow: '0 3px 6px rgba(168, 168, 168, 0.2)'
        },
        content: {
          background: '#ffffff',
          hoverBackground: '{surface.100}',
          borderColor: '#cccccc',
          color: '{surface.900}',
          hoverColor: '{surface.950}'
        },
        text: {
          color: '{surface.900}',
          hoverColor: '{surface.950}',
          mutedColor: '{surface.500}',
          hoverMutedColor: '{surface.600}'
        },
        overlay: {
          select: {
            background: '{surface.0}',
            borderColor: 'transparent',
            color: '{text.color}'
          },
          popover: {
            background: '{surface.0}',
            borderColor: 'transparent',
            color: '{text.color}'
          },
          modal: {
            background: '{surface.0}',
            borderColor: 'transparent',
            color: '{text.color}'
          }
        }
      }
    }
  },

  // ─── Component Tokens ───
  components: {
    datatable: {
      headerCell: {
        background: '#6d757a',
        hoverBackground: '#535154',
        selectedBackground: '#535154',
        borderColor: '#363636',
        color: '#ffffff',
        hoverColor: '#ffffff',
        selectedColor: '#ffffff',
        padding: '10px 5px'
      },
      bodyCell: {
        padding: '6px',
        borderColor: '#ffffff'
      },
      row: {
        stripedBackground: '#d4dadd'
      },
      sortIcon: {
        color: '#ffffff',
        hoverColor: '#ffffff'
      }
    },

    accordion: {
      header: {
        background: '#cc0000',
        hoverBackground: '#b30000',
        activeBackground: '#cc0000',
        activeHoverBackground: '#b30000',
        color: '#ffffff',
        hoverColor: '#ffffff',
        activeColor: '#ffffff',
        activeHoverColor: '#ffffff',
        padding: '1em 0.8em',
        fontWeight: '700',
        borderWidth: '0',
        borderColor: 'transparent',
        borderRadius: '0',
        toggleIcon: {
          color: '#ffffff',
          hoverColor: '#ffffff',
          activeColor: '#ffffff',
          activeHoverColor: '#ffffff'
        }
      },
      content: {
        borderWidth: '1px',
        borderColor: '#cccccc',
        background: '#ffffff',
        padding: '1.125rem'
      },
      panel: {
        borderWidth: '0',
        borderColor: 'transparent'
      }
    },

    toggleswitch: {
      root: {
        width: '5em',
        height: '20px',
        borderRadius: '30px',
        background: '#cccccc',
        hoverBackground: '#cccccc',
        checkedBackground: '#333333',
        checkedHoverBackground: '#333333',
        borderWidth: '0',
        borderColor: 'transparent',
        checkedBorderColor: 'transparent',
        checkedHoverBorderColor: 'transparent'
      },
      handle: {
        borderRadius: '50%',
        size: '1rem',
        background: '#ffffff',
        hoverBackground: '#ffffff',
        checkedBackground: '#ffffff',
        checkedHoverBackground: '#ffffff'
      }
    },

    toast: {
      root: {
        borderWidth: '0',
        borderRadius: '0'
      },
      content: {
        padding: '0.75rem',
        gap: '0'
      },
      closeButton: {
        width: '2rem',
        height: '2rem',
        borderRadius: '0'
      },
      closeIcon: {
        size: '1.125rem'
      },
      colorScheme: {
        light: {
          info: {
            background: '#3b86ff',
            borderColor: '#3b86ff',
            color: '#ffffff',
            detailColor: '#ffffff',
            shadow: 'none',
            closeButton: {
              hoverBackground: 'rgba(255,255,255,0.2)',
              focusRing: {
                color: '#ffffff',
                shadow: 'none'
              }
            }
          },
          success: {
            background: '#85B02E',
            borderColor: '#85B02E',
            color: '#ffffff',
            detailColor: '#ffffff',
            shadow: 'none',
            closeButton: {
              hoverBackground: 'rgba(255,255,255,0.2)',
              focusRing: {
                color: '#ffffff',
                shadow: 'none'
              }
            }
          },
          warn: {
            background: '#ffd814',
            borderColor: '#ffd814',
            color: '#212121',
            detailColor: '#212121',
            shadow: 'none',
            closeButton: {
              hoverBackground: 'rgba(0,0,0,0.1)',
              focusRing: {
                color: '#212121',
                shadow: 'none'
              }
            }
          },
          error: {
            background: '#cc0000',
            borderColor: '#cc0000',
            color: '#ffffff',
            detailColor: '#ffffff',
            shadow: 'none',
            closeButton: {
              hoverBackground: 'rgba(255,255,255,0.2)',
              focusRing: {
                color: '#ffffff',
                shadow: 'none'
              }
            }
          }
        }
      }
    },

    radiobutton: {
      root: {
        width: '20px',
        height: '20px',
        background: '#ffffff',
        checkedBackground: '#ffffff',
        checkedHoverBackground: '#ffffff',
        borderColor: '#d6d6d6',
        hoverBorderColor: '#d6d6d6',
        focusBorderColor: '#d6d6d6',
        checkedBorderColor: '#d6d6d6',
        checkedHoverBorderColor: '#d6d6d6',
        shadow: 'none'
      },
      icon: {
        checkedColor: '#cc0000',
        checkedHoverColor: '#cc0000'
      }
    },

    checkbox: {
      root: {
        width: '18px',
        height: '18px',
        background: '#ffffff',
        checkedBackground: '#ffffff',
        checkedHoverBackground: '#ffffff',
        borderColor: '#d6d6d6',
        hoverBorderColor: '#d6d6d6',
        focusBorderColor: '#d6d6d6',
        checkedBorderColor: '#d6d6d6',
        checkedHoverBorderColor: '#d6d6d6',
        shadow: 'none'
      },
      icon: {
        color: '#cc0000',
        checkedColor: '#cc0000',
        checkedHoverColor: '#cc0000'
      }
    },

    chip: {
      colorScheme: {
        light: {
          root: {
            background: '#186ba0',
            color: '#ffffff'
          },
          icon: {
            color: '#ffffff'
          },
          removeIcon: {
            color: '#ffffff'
          }
        }
      }
    },

    autocomplete: {
      root: {
        focusBorderColor: '#186ba0',
        focusRing: {
          width: '{focus.ring.width}',
          style: '{focus.ring.style}',
          color: '{focus.ring.color}',
          offset: '{focus.ring.offset}',
          shadow: '{focus.ring.shadow}'
        }
      },
      option: {
        focusBackground: '#f1322e',
        selectedBackground: '#f1322e',
        selectedFocusBackground: '#cc0000',
        focusColor: '#ffffff',
        selectedColor: '#ffffff',
        selectedFocusColor: '#ffffff',
        borderRadius: '0'
      },
      overlay: {
        background: '#ffffff',
        borderColor: '#d6d6d6',
        borderRadius: '0'
      }
    },

    select: {
      option: {
        focusBackground: '#f1322e',
        selectedBackground: '#f1322e',
        selectedFocusBackground: '#cc0000',
        color: '{text.color}',
        focusColor: '#ffffff',
        selectedColor: '#ffffff',
        selectedFocusColor: '#ffffff',
        borderRadius: '0'
      },
      overlay: {
        background: '#ffffff',
        borderColor: '#d6d6d6',
        color: '{text.color}',
        borderRadius: '0'
      }
    },

    progressbar: {
      value: {
        background: '#707070'
      },
      root: {
        borderRadius: '0',
        height: '2rem'
      },
      label: {
        fontSize: '0.875rem',
        fontWeight: '400',
        color: '#000000'
      }
    },

    tabs: {
      tab: {
        background: '#ffffff',
        hoverBackground: '#6d757a',
        activeBackground: '#6d757a',
        color: '#000000',
        hoverColor: '#ffffff',
        activeColor: '#ffffff',
        padding: '0.3em 1.4em',
        fontWeight: '600',
        borderWidth: '0',
        borderColor: 'transparent',
        activeBorderColor: 'transparent'
      },
      tablist: {
        borderWidth: '0',
        background: 'transparent'
      },
      activeBar: {
        background: 'transparent'
      },
      tabpanel: {
        padding: '0'
      }
    },

    paginator: {
      navButton: {
        background: 'transparent',
        hoverBackground: '{surface.200}',
        selectedBackground: '#6d757a',
        color: '{surface.700}',
        hoverColor: '{surface.900}',
        selectedColor: '#ffffff',
        borderRadius: '0'
      }
    },

    slider: {
      track: {
        background: '#c4c4c4',
        borderRadius: '0',
        size: '0.8em'
      },
      handle: {
        width: '2em',
        height: '2em',
        background: '#ffffff',
        hoverBackground: '#ffffff',
        borderRadius: '50%'
      },
      range: {
        background: '#3794bf'
      }
    }
  }
});

export default VidaPreset;
